import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { Users, X, RefreshCw, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface Course {
  id: number
  name: string
  fach: string
  jahrgang: string
}

interface Group {
  id: number
  name: string
  gruppen_typ: string
}

interface Participant {
  id: string
  first_name: string
  last_name: string
  klasse: string
}

interface ParticipantsModalProps {
  type: 'course' | 'group' | 'class'
  item: Course | Group | Class
  onClose: () => void
  onRemoveStudent: (studentId: string) => void
  isLoading: boolean
  /** Stichtag für Mitgliederliste (YYYY-MM-DD); null = heute */
  standAsOf?: string | null
}

interface Class {
  id: number
  class_name: string
  name?: string
}

export default function ParticipantsModal({ type, item, onClose, onRemoveStudent, isLoading, standAsOf }: ParticipantsModalProps) {
  const [participants, setParticipants] = useState<Participant[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        setLoading(true)
        const endpoint = `/memberships/units/${type}/${item.id}/members`
        const params = standAsOf ? { date: standAsOf } : {}
        const response = await api.get(endpoint, { params })
        
        // Transformiere die Daten in das erwartete Format
        const members = response.data.data || []
        const transformedParticipants = members.map((member: any) => ({
          id: member.sophomorixUnid || member.Student_ID || member.username || String(member.user_id),
          first_name: member.Vorname || member.first_name || '',
          last_name: member.Name || member.last_name || '',
          klasse: member.Klasse || ''
        }))
        
        setParticipants(transformedParticipants)
      } catch (error) {
        console.error('Error fetching participants:', error)
        toast.error('Fehler beim Laden der Teilnehmer')
      } finally {
        setLoading(false)
      }
    }

    fetchParticipants()
  }, [type, item.id, standAsOf])

  // Filter participants based on search term
  const filteredParticipants = participants.filter(participant => {
    if (!participant) return false
    
    const fullName = `${participant.first_name || ''} ${participant.last_name || ''}`.toLowerCase()
    const klasse = (participant.klasse || '').toLowerCase()
    const search = searchTerm.toLowerCase()
    return fullName.includes(search) || klasse.includes(search)
  })

  const toggleParticipant = (participantId: string) => {
    const newSelection = new Set(selectedParticipants)
    if (newSelection.has(participantId)) {
      newSelection.delete(participantId)
    } else {
      newSelection.add(participantId)
    }
    setSelectedParticipants(newSelection)
  }

  const handleSelectAll = () => {
    if (selectedParticipants.size === filteredParticipants.length) {
      // Deselect all
      setSelectedParticipants(new Set())
    } else {
      // Select all filtered participants
      setSelectedParticipants(new Set(filteredParticipants.map(p => p.id)))
    }
  }

  const handleBulkRemove = () => {
    if (selectedParticipants.size === 0) {
      toast.error('Bitte wählen Sie mindestens einen Teilnehmer aus')
      return
    }

    const participantNames = participants
      .filter(p => selectedParticipants.has(p.id))
      .map(p => `${p.first_name} ${p.last_name}`)
      .join(', ')

    if (window.confirm(`Möchten Sie ${selectedParticipants.size} Teilnehmer wirklich entfernen?\n\n${participantNames}`)) {
      // Remove participants one by one
      selectedParticipants.forEach(participantId => {
        onRemoveStudent(participantId)
      })
      setSelectedParticipants(new Set())
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
      <div className="card flex max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
            Teilnehmer von {type === 'course' ? 'Kurs' : type === 'group' ? 'Gruppe' : 'Klasse'} verwalten
          </h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title="Schließen"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {item?.name || (item as any)?.class_name} - {type === 'course' ? 'Kurs' : type === 'group' ? 'Gruppe' : 'Klasse'}
        </p>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field"
              placeholder="Nach Name oder Klasse suchen..."
            />
          </div>
        </div>

        {/* Select All Button */}
        <div className="mb-4">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            {selectedParticipants.size === filteredParticipants.length && filteredParticipants.length > 0 
              ? 'Alle abwählen' 
              : 'Alle auswählen'
            }
          </button>
          <span className="text-sm text-gray-500 ml-2">
            ({selectedParticipants.size} von {filteredParticipants.length} ausgewählt)
          </span>
        </div>

        {/* Bulk Remove Button */}
        {selectedParticipants.size > 0 && (
          <div className="mb-4">
            <button
              onClick={handleBulkRemove}
              disabled={isLoading}
              className="px-4 py-2 text-sm bg-red-100 text-red-700 border border-red-300 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="w-4 h-4 inline mr-2" />
              Ausgewählte entfernen ({selectedParticipants.size})
            </button>
          </div>
        )}

        {/* Participants List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 text-gray-400 animate-spin" />
              <p className="text-gray-500">Teilnehmer werden geladen...</p>
            </div>
          ) : filteredParticipants.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">
                {searchTerm ? 'Keine Teilnehmer gefunden' : `Keine Teilnehmer in dieser ${type === 'course' ? 'Kurs' : type === 'group' ? 'Gruppe' : 'Klasse'}`}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredParticipants.map((participant) => (
                <div
                  key={participant.id}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedParticipants.has(participant.id)
                      ? 'bg-primary-50 border-primary-200'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => toggleParticipant(participant.id)}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                      selectedParticipants.has(participant.id)
                        ? 'bg-primary-600 border-primary-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedParticipants.has(participant.id) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {participant.first_name || 'Unbekannt'} {participant.last_name || 'Name'}
                      </h4>
                      <p className="text-xs text-gray-600">
                        Klasse {participant.klasse || 'Unbekannt'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  )
}